import { Color } from '@persona-id/design-tokens';
import { flatten, includes, kebabCase, snakeCase } from 'lodash';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { ExportConfigStruct } from 'dashboard/components/GraphExportModalV2/GraphExportConfig';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import GraphTextTags from 'dashboard/components/GraphTextTags';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { getHydratedNodeDisplayValue } from 'dashboard/lib/utils/graph';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import { AccountType } from 'dashboard/models/account-type';
import {
  DatumType,
  HashableNodeId,
  NodeId,
  nodeIdFromHashable,
  nodeIdToHashable,
} from 'dashboard/models/graph';
import { convertServerNodeToNodeId, ServerNHopsResponse } from 'dashboard/models/graph-backend';
import { GraphExplorer } from 'dashboard/models/graph-explorer';
import { HydratedAccount, HydratedNode } from 'dashboard/models/graph-hydration';
import { GraphQuery, GraphQueryStatus, QueryType } from 'dashboard/models/graph-query';
import Sticker from 'design-system/components/Sticker/Sticker';
import { ACCOUNT_DATUM_TYPE, ACTIVE_ACCOUNT_STATUS } from 'lib/constants/graph';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

export enum GraphExporterType {
  SelectedAccounts = 'selected-accounts',
  ShortestPathFromSeedSet = 'shortest-path-from-seed-set',
}

export type GraphExporter = SelectedAccountsGraphExporter | ShortestPathFromSeedSetGraphExporter;

export interface BaseGraphExporter {
  type: GraphExporterType;
  label: string;
  // if valid, returns null, otherwise, returns the invalid message
  invalidMessage: (graphQuery?: GraphQuery | null) => string | null;
  // given a list of account ids to export, fetch and generate the appropriate data to export
  generate: (
    exportedAccountIds: NodeId[],
    graphExplorer: GraphExplorer,
    getHydratedNodes: (
      nodeIds: NodeId[],
      includeAccountFields: boolean,
    ) => Promise<Map<HashableNodeId, HydratedNode>>,
    onCompletion: () => void,
    graphQuery?: GraphQuery,
  ) => Promise<void>;
  generateWithConfig?: (
    exportedAccountIds: NodeId[],
    config: ExportConfigStruct,
    graphExplorer: GraphExplorer,
    accountTypes: AccountType[],
    getHydratedNodes: (
      nodeIds: NodeId[],
      includeAccountFields: boolean,
    ) => Promise<Map<HashableNodeId, HydratedNode>>,
    onCompletion: (result: Record<string, string | string[]>[]) => void,
  ) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any[];
  useExportV2: boolean;
}

interface SelectedAccountsExportDatum {
  id: string;
  'reference-id': string;
  'created-at': string;
  status: string;
  name: string[];
  tags?: string[];
  'inquiries-approved'?: number;
  'inquiries-declined'?: number;
  'inquiries-needs-review'?: number;
  'inquiries-completed'?: number;
  'inquiries-failed'?: number;
  'inquiries-expired'?: number;
  'inquiries-created'?: number;
  'inquiries-pending'?: number;
  email?: string[];
  address?: string[];
  device_fingerprint?: string[];
  browser_fingerprint?: string[];
  device_vendor_id?: string[];
  network?: string[];
  phone?: string[];
  government_id?: string[];
  birthdate?: string[];
  [custom_data_type: string]: any;
}

const customDatumTypeToColumnHeader = (datumType: DatumType): string => {
  return snakeCase(datumType.name);
};

const accountFieldToColumnHeader = (accountTypeName: string, fieldName: string): string => {
  return `${accountTypeName} - ${snakeCase(fieldName)}`;
};

export class SelectedAccountsGraphExporter implements BaseGraphExporter {
  type: GraphExporterType.SelectedAccounts;
  label: string;
  result: SelectedAccountsExportDatum[];
  useExportV2: boolean;

  constructor(useExportV2 = false) {
    this.type = GraphExporterType.SelectedAccounts;
    this.label = 'Selected Accounts';
    this.result = [];
    this.useExportV2 = useExportV2;
  }

  invalidMessage(_graphQuery?: GraphQuery | null): string | null {
    return null;
  }

  async generateWithConfig(
    exportedAccountIds: NodeId[],
    config: ExportConfigStruct,
    graphExplorer: GraphExplorer,
    accountTypes: AccountType[],
    getHydratedNodes: (
      nodeIds: NodeId[],
      includeAccountFields: boolean,
    ) => Promise<Map<HashableNodeId, HydratedNode>>,
    onCompletion: (result: Record<string, string | string[]>[]) => void,
  ) {
    // Generate column headers
    const datumTypeColumnHeaders: string[] = config.datumTypes.map((dtToken) => {
      const datumType = graphExplorer.getDatumTypeByToken(dtToken);
      return datumType.isCustomType ? customDatumTypeToColumnHeader(datumType) : datumType.name;
    });

    const accountFieldColumnHeaders: string[][] = [];
    const accountTypeNames = accountTypes.reduce(
      (result: { [token: string]: string }, accountType) => {
        result[accountType.id] = accountType.attributes.name;
        return result;
      },
      {},
    );
    Object.keys(config.fieldsByAccountType).forEach((token) => {
      accountFieldColumnHeaders.push(
        config.fieldsByAccountType[token].map((fieldKey) =>
          accountFieldToColumnHeader(accountTypeNames[token] || token, fieldKey),
        ),
      );
    });

    const allColumnHeaders: string[] = [
      ...datumTypeColumnHeaders,
      ...flatten(accountFieldColumnHeaders),
    ];

    // Get account and neighbor hydrated nodes
    const filteredExportedAccountIds = exportedAccountIds.filter(
      (nodeId) => nodeId.token === ACCOUNT_DATUM_TYPE.token,
    );

    const neighborsToHydrate: NodeId[][] = filteredExportedAccountIds.map((accountNodeId: NodeId) =>
      graphExplorer.getAdjacentPropertyNodes(accountNodeId).map(nodeIdFromHashable),
    );

    const hydratedNodes = await getHydratedNodes(
      [...filteredExportedAccountIds, ...neighborsToHydrate.flat()],
      this.useExportV2,
    );

    // Create export data
    const exportData: Record<string, string | string[]>[] = filteredExportedAccountIds.map(
      (accountNodeId, i) => {
        const hydratedAccountNode: HydratedAccount = hydratedNodes.get(
          nodeIdToHashable(accountNodeId),
        ) as HydratedAccount;

        // Set hard-coded values
        let exportDataRow: Record<string, string | string[]> = {};
        exportDataRow['id'] = accountNodeId.value;
        exportDataRow['name'] = []; // Fill this if there are no neighboring name nodes
        exportDataRow['created-at'] = DateTime.fromISO(
          hydratedAccountNode?.hydrationData['created-at'],
        ).toFormat(DATE_AND_TIME_FORMAT);
        exportDataRow['reference-id'] = hydratedAccountNode?.hydrationData['reference-id'];
        exportDataRow['status'] = hydratedAccountNode?.hydrationData.status;

        // Set generated column headers
        exportDataRow = allColumnHeaders.reduce(
          (result: Record<string, string | string[]>, header) => {
            result[header] = [];
            return result;
          },
          exportDataRow,
        );

        // Set value for datum types
        for (const neighbour of neighborsToHydrate[i]) {
          const hydratedNeighbour = hydratedNodes.get(nodeIdToHashable(neighbour));
          if (hydratedNeighbour) {
            const datumType = graphExplorer.getDatumTypeByToken(neighbour.token);
            if (includes(config.datumTypes, datumType.token)) {
              const columnHeader = datumType.isCustomType
                ? customDatumTypeToColumnHeader(datumType)
                : datumType.name;

              exportDataRow[columnHeader] = [
                ...exportDataRow[columnHeader],
                getHydratedNodeDisplayValue(hydratedNeighbour),
              ];
            }
          }
        }

        // Set value for account fields
        if (hydratedAccountNode.hydrationData['fields-data']) {
          const accountTypeToken =
            hydratedAccountNode.hydrationData['fields-data']['account-type-token'];
          if (accountTypeToken in config.fieldsByAccountType) {
            config.fieldsByAccountType[accountTypeToken].forEach((fieldKey) => {
              const fieldValue =
                hydratedAccountNode.hydrationData?.['fields-data']?.['values-map']?.[
                  kebabCase(fieldKey)
                ];

              let rowValue = '';
              if (typeof fieldValue === 'object') {
                rowValue = JSON.stringify(fieldValue).replace(/"/g, '""');
              } else if (typeof fieldValue === 'string') {
                rowValue = fieldValue.replace(/"/g, '""');
              } else if (fieldValue) {
                rowValue = fieldValue;
              }

              exportDataRow[
                accountFieldToColumnHeader(
                  accountTypeNames[accountTypeToken] || accountTypeToken,
                  fieldKey,
                )
              ] = rowValue;
            });
          }
        }

        // Fallback name to what is hydrated on Account
        if (exportDataRow.name.length === 0) {
          exportDataRow['name'] = [
            [
              hydratedAccountNode?.hydrationData.name.first,
              hydratedAccountNode?.hydrationData.name.middle,
              hydratedAccountNode?.hydrationData.name.last,
            ]
              .filter(Boolean)
              .join(' '),
          ];
        }

        return exportDataRow;
      },
    );

    onCompletion(exportData);
  }

  async generate(
    exportedAccountIds: NodeId[],
    graphExplorer: GraphExplorer,
    getHydratedNodes: (
      nodeIds: NodeId[],
      includeAccountFields: boolean,
    ) => Promise<Map<HashableNodeId, HydratedNode>>,
    onCompletion: () => void,
  ) {
    this.result = [];

    // filter out all nonAccounts (sanity check)
    let filteredExportedAccountIds = exportedAccountIds.filter(
      (nodeId) => nodeId.token === ACCOUNT_DATUM_TYPE.token,
    );

    // for V2, this method is used to generate the preview and not the actual csv data set.
    if (this.useExportV2) {
      filteredExportedAccountIds = filteredExportedAccountIds.slice(0, 10);
    }

    const neighborsToHydrate: NodeId[][] = this.useExportV2
      ? []
      : filteredExportedAccountIds.map((accountNodeId: NodeId) =>
          graphExplorer.getAdjacentPropertyNodes(accountNodeId).map(nodeIdFromHashable),
        );

    // hydrate all relevant account nodes and its neightbours
    const hydratedNodes = await getHydratedNodes(
      [...filteredExportedAccountIds, ...neighborsToHydrate.flat()],
      this.useExportV2,
    );

    // generate a row for each account
    const exportData = filteredExportedAccountIds.map((accountNodeId, i) => {
      const hydratedAccountNode: HydratedAccount = hydratedNodes.get(
        nodeIdToHashable(accountNodeId),
      ) as HydratedAccount;

      const baseExportData = {
        name: [],
        id: accountNodeId.value,
        'created-at': DateTime.fromISO(hydratedAccountNode?.hydrationData['created-at']).toFormat(
          DATE_AND_TIME_FORMAT,
        ),
        'reference-id': hydratedAccountNode?.hydrationData['reference-id'],
        status: hydratedAccountNode?.hydrationData.status,
      };

      const exportDataRow: SelectedAccountsExportDatum = this.useExportV2
        ? baseExportData
        : {
            ...baseExportData,
            tags: hydratedAccountNode?.hydrationData.tags,
            'inquiries-approved': hydratedAccountNode?.hydrationData['inquiry-counts']['approved'],
            'inquiries-declined': hydratedAccountNode?.hydrationData['inquiry-counts']['declined'],
            'inquiries-needs-review':
              hydratedAccountNode?.hydrationData['inquiry-counts']['needs-review'],
            'inquiries-completed':
              hydratedAccountNode?.hydrationData['inquiry-counts']['completed'],
            'inquiries-failed': hydratedAccountNode?.hydrationData['inquiry-counts']['failed'],
            'inquiries-expired': hydratedAccountNode?.hydrationData['inquiry-counts']['expired'],
            'inquiries-created': hydratedAccountNode?.hydrationData['inquiry-counts']['created'],
            'inquiries-pending': hydratedAccountNode?.hydrationData['inquiry-counts']['pending'],
            email: [],
            address: [],
            device_fingerprint: [],
            browser_fingerprint: [],
            device_vendor_id: [],
            network: [],
            phone: [],
            government_id: [],
            birthdate: [],
          };

      if (!this.useExportV2) {
        // add custom data types to account row
        const customDatumTypeNames = graphExplorer
          .getNodeDatumTypes()
          .map(customDatumTypeToColumnHeader);
        for (const datumTypeName of customDatumTypeNames) {
          exportDataRow[datumTypeName] = [];
        }

        // populate account properties by hydrating properties nodes
        for (const neighbour of neighborsToHydrate[i]) {
          const hydratedNeighbour = hydratedNodes.get(nodeIdToHashable(neighbour));
          const datumType = graphExplorer.getDatumTypeByToken(neighbour.token);

          const columnHeader = datumType.isCustomType
            ? customDatumTypeToColumnHeader(datumType)
            : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              hydratedNeighbour.nodeType;

          exportDataRow[columnHeader] = [
            ...exportDataRow[columnHeader],
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            getHydratedNodeDisplayValue(hydratedNeighbour),
          ];
        }
      }

      // fallback name to what is hydrated on Account
      if (!this.useExportV2 && exportDataRow.name.length === 0) {
        exportDataRow.name.push(
          [
            hydratedAccountNode?.hydrationData.name.first,
            hydratedAccountNode?.hydrationData.name.middle,
            hydratedAccountNode?.hydrationData.name.last,
          ]
            .filter(Boolean)
            .join(' '),
        );
      }

      return exportDataRow;
    });

    this.result = exportData;
    onCompletion();
  }
}

const NoWrapTableTR = styled(TableTR)`
  white-space: nowrap;
`;

const ConstrainedTableTD = styled(TableTD)`
  max-width: 600px;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const AccountIdTableTH = styled(TableTH)`
  border-right: 1px solid ${Color.CoreSlate100};
`;

const AccountIdTableTD = styled(TableTD)`
  border-right: 1px solid ${Color.CoreSlate100};
`;

interface SelectedAccountsGraphExporterPreviewProps {
  exporter: SelectedAccountsGraphExporter;
}

export const SelectedAccountsGraphExporterPreview = ({
  exporter,
}: SelectedAccountsGraphExporterPreviewProps) => {
  const hydratedExportData = exporter.result;
  const showAccountStatus = useFeatureFlag('dashboard:accounts-show-account-status');
  const useExportV2 = useFeatureFlag('dashboard:graph-use-export-v2');
  const { graphExplorer } = useGraphExplorer();
  const customDataTypes = graphExplorer.getNodeDatumTypes();

  return (
    <TableLayout
      dataTest='graph-explorer__export-modal__table'
      containerMaxHeight={600}
      headerTR={
        <>
          <AccountIdTableTH>Account ID</AccountIdTableTH>
          <TableTH>Name</TableTH>
          <TableTH>Created At</TableTH>
          <TableTH>Reference ID</TableTH>
          {showAccountStatus && <TableTH>Status</TableTH>}
          {!useExportV2 && <TableTH>Tags</TableTH>}
          {!useExportV2 && <TableTH>Email Address</TableTH>}
          {!useExportV2 && <TableTH>Address</TableTH>}
          {!useExportV2 && <TableTH>Device Fingerprint</TableTH>}
          {!useExportV2 && <TableTH>Browser Fingerprint</TableTH>}
          {!useExportV2 && <TableTH>Device Vendor ID</TableTH>}
          {!useExportV2 && <TableTH>IP Address</TableTH>}
          {!useExportV2 && <TableTH>Phone Number</TableTH>}
          {!useExportV2 && <TableTH>ID Number</TableTH>}
          {!useExportV2 && <TableTH>Birthdate</TableTH>}
          {!useExportV2 &&
            customDataTypes.map((datumType) => {
              return (
                <TableTH key={`preview-${datumType.name}-column`}>
                  {titleCase(datumType.name)}
                </TableTH>
              );
            })}
          {!useExportV2 && <TableTH>Inquiries Declined</TableTH>}
          {!useExportV2 && <TableTH>Inquiries Approved</TableTH>}
          {!useExportV2 && <TableTH>Inquiries Completed</TableTH>}
          {!useExportV2 && <TableTH>Inquiries Failed</TableTH>}
          {!useExportV2 && <TableTH>Inquiries Expired</TableTH>}
          {!useExportV2 && <TableTH>Inquiries Pending</TableTH>}
          {!useExportV2 && <TableTH>Inquiries with Created status</TableTH>}
          {!useExportV2 && <TableTH>Inquiries with Needs Review status</TableTH>}
        </>
      }
      isEmpty={(hydratedExportData || []).length === 0}
      nullState={
        <TableNullState dataTestBlock='graph-explorer__export-modal__null-table'>
          <h3>No results.</h3>
        </TableNullState>
      }
    >
      {hydratedExportData.map((accountExportDatum: SelectedAccountsExportDatum) => {
        return (
          <NoWrapTableTR key={accountExportDatum.id}>
            <AccountIdTableTD>{accountExportDatum.id}</AccountIdTableTD>
            <ConstrainedTableTD>
              {Array.from(accountExportDatum.name).join('; ')}
            </ConstrainedTableTD>
            <ConstrainedTableTD>{accountExportDatum['created-at']}</ConstrainedTableTD>
            <ConstrainedTableTD>{accountExportDatum['reference-id']}</ConstrainedTableTD>
            {showAccountStatus && (
              <ConstrainedTableTD>
                {accountExportDatum.status && (
                  <Sticker
                    variant={
                      accountExportDatum.status === ACTIVE_ACCOUNT_STATUS ? 'success' : 'basic'
                    }
                  >
                    {accountExportDatum.status}
                  </Sticker>
                )}
              </ConstrainedTableTD>
            )}
            {!useExportV2 && (
              <>
                <ConstrainedTableTD>
                  {accountExportDatum.tags && <GraphTextTags tags={accountExportDatum.tags} />}
                </ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum.email?.join('; ')}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum.address?.join('; ')}</ConstrainedTableTD>
                <ConstrainedTableTD>
                  {accountExportDatum['device_fingerprint']?.join('; ')}
                </ConstrainedTableTD>
                <ConstrainedTableTD>
                  {accountExportDatum['browser_fingerprint']?.join('; ')}
                </ConstrainedTableTD>
                <ConstrainedTableTD>
                  {accountExportDatum['device_vendor_id']?.join('; ')}
                </ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum['network']?.join('; ')}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum.phone?.join('; ')}</ConstrainedTableTD>
                <ConstrainedTableTD>
                  {accountExportDatum.government_id?.join('; ')}
                </ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum.birthdate?.join('; ')}</ConstrainedTableTD>
                {customDataTypes.map((datumType) => {
                  const columnHeader = customDatumTypeToColumnHeader(datumType);
                  return (
                    <ConstrainedTableTD key={`preview-${datumType.name}-column-cell`}>
                      {accountExportDatum[columnHeader].join('; ')}
                    </ConstrainedTableTD>
                  );
                })}
                <ConstrainedTableTD>{accountExportDatum['inquiries-declined']}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum['inquiries-approved']}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum['inquiries-completed']}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum['inquiries-failed']}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum['inquiries-expired']}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum['inquiries-pending']}</ConstrainedTableTD>
                <ConstrainedTableTD>{accountExportDatum['inquiries-created']}</ConstrainedTableTD>
                <ConstrainedTableTD>
                  {accountExportDatum['inquiries-needs-review']}
                </ConstrainedTableTD>
              </>
            )}
          </NoWrapTableTR>
        );
      })}
    </TableLayout>
  );
};

type AccountHash = HashableNodeId;
type PropertyHash = HashableNodeId;

interface PrunedAccountNode {
  // min hops to get to this account node from seed account
  minHops: number;
  // The shortest path from a seed account to any account in this map is through this account
  // The value in the map are the property nodes the account nodes are connected by
  connectedTo: Map<AccountHash, PropertyHash[]>;
}

interface ShortestPathFromSeedSetExportRow {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [coloumnHeader: string]: any;
}

interface ShortestPathFromSeedSetExportRowPart {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [coloumnHeader: string]: any;
}

export class ShortestPathFromSeedSetGraphExporter implements BaseGraphExporter {
  type: GraphExporterType.ShortestPathFromSeedSet;
  label: string;
  result: ShortestPathFromSeedSetExportRow[];
  // longest number of hops from seed account to a connected account
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  longestPath: number;

  constructor() {
    this.type = GraphExporterType.ShortestPathFromSeedSet;
    this.label = 'Shortest Path From Seed Set';
    this.result = [];
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  invalidMessage(currentGraphQuery: GraphQuery | null) {
    // only allow this export format for completed nhop queries
    if (!currentGraphQuery) {
      return 'Cannot find corresponding Graph Query';
    }
    if (currentGraphQuery.attributes.params.type !== QueryType.NHopsFromAccount) {
      return 'Can only export results from NHop Queries';
    }
    if (currentGraphQuery.attributes.status !== GraphQueryStatus.Completed) {
      return 'Corresponding Graph Query is not completed';
    }
    const seedSet = (currentGraphQuery.attributes.result as ServerNHopsResponse)['seed-set'];
    if (!seedSet || seedSet.length === 0) {
      return 'Graph Query must have a seed set';
    }
    return null;
  }

  getAccountRowPartPrefix(numHops: number) {
    if (numHops === 0) {
      return 'seed-account';
    }
    return `account-${numHops}-hops-away`;
  }

  // DFS is linear to the number of paths, which can explode exponentially :(
  async generate(
    _exportedAccountIds: NodeId[],
    graphExplorer: GraphExplorer,
    getHydratedNodes: (
      nodeIds: NodeId[],
      includeAccountFields: boolean,
    ) => Promise<Map<HashableNodeId, HydratedNode>>,
    onCompletion: () => void,
    graphQuery?: GraphQuery,
  ) {
    if (!graphQuery) {
      this.result = [];
      this.longestPath = 0;
      onCompletion();
      return;
    }

    // TODO (bjon): On my crafting, seedSet sometimes contains duplicate elements.
    // For now, I create a dedupped list of seed nodes, but it should not do that to begin with
    const seedAccounts = [
      ...new Set(
        (graphQuery.attributes.result as ServerNHopsResponse)['seed-set']
          .map(convertServerNodeToNodeId)
          .map(nodeIdToHashable),
      ),
    ];

    // generate pruned account nodes in a BFS
    const prunedAccountNodes: Map<HashableNodeId, PrunedAccountNode> = new Map();
    // generate prunedAccountNodes for seed accounts and add them to the q
    for (const seedAccount of seedAccounts) {
      prunedAccountNodes.set(seedAccount, { minHops: 0, connectedTo: new Map() });
    }
    const q: HashableNodeId[] = [...seedAccounts];

    let currentNumHops = 0;
    let longestPath = 0;
    while (q.length > 0) {
      // now traversing through nodes 2 hops further away from seed account
      currentNumHops += 2;
      const numAccountsToCheck = q.length;
      for (let i = 0; i < numAccountsToCheck; i++) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const currentAccount: HashableNodeId = q.shift();

        // find all properties connected to the account
        const connectedProperties: HashableNodeId[] = graphExplorer.getAdjacentPropertyNodes(
          nodeIdFromHashable(currentAccount),
        );
        for (const connectedProperty of connectedProperties) {
          // find all accounts connected to current account by this property
          const adjacentAccounts = graphExplorer.getAdjacentAccountNodes(
            nodeIdFromHashable(connectedProperty),
          );
          for (const adjacentAccount of adjacentAccounts) {
            // skip if we traversed to this node with fewer hops. This will also handle the case
            // when currentAccount = adjacentAccount
            if (prunedAccountNodes.has(adjacentAccount)) {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              if (prunedAccountNodes.get(adjacentAccount).minHops < currentNumHops) {
                continue;
              }
            }
            // if we have not yet seen this node, update minHops and add it to the q
            else {
              prunedAccountNodes.set(adjacentAccount, {
                minHops: currentNumHops,
                connectedTo: new Map(),
              });
              q.push(adjacentAccount);
              longestPath = currentNumHops;
            }
            // record that currentAccount is connected to adjacentAccount by connectedProperty
            const currentPrunedAccount = prunedAccountNodes.get(currentAccount);
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const connectedTo = currentPrunedAccount.connectedTo.get(adjacentAccount) || [];
            connectedTo.push(connectedProperty);
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            currentPrunedAccount.connectedTo.set(adjacentAccount, connectedTo);
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            prunedAccountNodes.set(currentAccount, currentPrunedAccount);
          }
        }
      }
    }

    // hydrate all relevant nodes
    const hydratedNodes = await getHydratedNodes(
      [...prunedAccountNodes.keys()].map(nodeIdFromHashable),
      false,
    );

    // perform dfs from each seed node to generate all paths
    const result: ShortestPathFromSeedSetExportRow[] = [];

    const generatePaths = (
      exportRowParts: ShortestPathFromSeedSetExportRowPart[],
      currentAccount: HashableNodeId,
      numHops: number,
    ) => {
      const prunedAccount = prunedAccountNodes.get(currentAccount);
      // if not connected to any more nodes, generate export row from current path
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (prunedAccount.connectedTo.size === 0) {
        // create a export row by merging all parts collected
        const row: ShortestPathFromSeedSetExportRow = {};
        for (const part of exportRowParts) {
          Object.assign(row, part);
        }
        result.push(row);
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        for (const [nextAccount, connectedBy] of prunedAccount.connectedTo.entries()) {
          const hydratedNextAccount = hydratedNodes.get(nextAccount) as HydratedAccount;
          const nextNodeFields = this.convertAccountToRowPart(
            graphExplorer,
            nextAccount,
            hydratedNextAccount,
            numHops,
            connectedBy,
          );

          exportRowParts.push(nextNodeFields);
          generatePaths(exportRowParts, nextAccount, numHops + 2);
          // backtracking
          exportRowParts.pop();
        }
      }
    };

    for (const seedAccount of seedAccounts) {
      const hydratedSeedNode = hydratedNodes.get(seedAccount) as HydratedAccount;
      const exportRowParts = [
        this.convertAccountToRowPart(graphExplorer, seedAccount, hydratedSeedNode, 0),
      ];
      generatePaths(exportRowParts, seedAccount, 2);
    }

    this.result = result;
    this.longestPath = longestPath;
    onCompletion();
  }

  // Generate the set of all shortest paths from a seed account to all connected accounts
  // If there are multiple shortest paths from a seed account to a connected account, ALL of them are included
  // We do not include a shortest path if it is part of a shortest path to another account
  // Ex: If A -> B -> C is a shortest path, A -> B is not included
  // Seed accounts are based on the current Graph Query. We do not care what nodes are highlighted/selected

  // Implementation:
  // The algorithm first prunes the graph into a directed graph such that any arc AB is in the graph iff a shortest path
  // from a seed account to B goes through A. We use a BFS to find all such arcs and generate a set of PrunedAccountNodes
  // When we traverse the resulting digraph and generate a path P from a seed account to some other account A, that path must
  // be a shortest path. We use a DFS to generate all such paths. Each path then gets converted to an export row.

  // Time complexity-wise, BFS is linear to the number of accounts and edges

  // generate part of an export row from a hydrated account
  private convertAccountToRowPart(
    graphExplorer: GraphExplorer,
    accountId: HashableNodeId,
    hydratedAccountNode: HydratedAccount,
    numHops: number,
    connectedByProperties: HashableNodeId[] = [],
  ): ShortestPathFromSeedSetExportRowPart {
    const prefix = this.getAccountRowPartPrefix(numHops);
    const rowPart: ShortestPathFromSeedSetExportRowPart = {};

    rowPart[`${prefix}-id`] = nodeIdFromHashable(accountId).value;
    if (connectedByProperties.length > 0) {
      // replace token to human-readable property name
      rowPart[`${prefix}-connected-by`] = JSON.stringify(
        connectedByProperties.map((nodeHash) => {
          const nodeId = nodeIdFromHashable(nodeHash);
          const datumType = graphExplorer.getDatumTypeByToken(nodeId.token);

          return {
            node: snakeCase(datumType.name),
            value: nodeId.value,
          };
        }),
      );
    }
    rowPart[`${prefix}-name`] = [
      [
        hydratedAccountNode?.hydrationData.name.first,
        hydratedAccountNode?.hydrationData.name.middle,
        hydratedAccountNode?.hydrationData.name.last,
      ]
        .filter(Boolean)
        .join(' '),
    ];
    rowPart[`${prefix}-created-at`] = DateTime.fromISO(
      hydratedAccountNode?.hydrationData['created-at'],
    ).toFormat(DATE_AND_TIME_FORMAT);
    rowPart[`${prefix}-reference-id`] = hydratedAccountNode?.hydrationData['reference-id'];
    rowPart[`${prefix}-status`] = hydratedAccountNode?.hydrationData.status;
    rowPart[`${prefix}-tags`] = hydratedAccountNode?.hydrationData.tags;
    return rowPart;
  }
}

interface ShortestPathFromSeedSetGraphExporterPreviewHeaderProps {
  longestPath: number;
}

const ShortestPathFromSeedSetGraphExporterPreviewHeader = ({
  longestPath,
}: ShortestPathFromSeedSetGraphExporterPreviewHeaderProps) => {
  const showAccountStatus = useFeatureFlag('dashboard:accounts-show-account-status');

  const getTableColumnPrefix = (numHops: number) => {
    if (numHops === 0) {
      return 'Seed Account';
    }
    return `Account ${numHops} Hops Away`;
  };

  const header = [];
  for (let hop = 0; hop <= longestPath; hop += 2) {
    const prefix = getTableColumnPrefix(hop);
    header.push(
      <>
        <TableTH>{prefix} Account ID</TableTH>
        {hop > 0 && <TableTH>{prefix} Connected By</TableTH>}
        <TableTH>{prefix} Name</TableTH>
        <TableTH>{prefix} Created At</TableTH>
        <TableTH>{prefix} Reference ID</TableTH>
        {showAccountStatus && <TableTH>{prefix} Status</TableTH>}
        <TableTH>{prefix} Tags</TableTH>
      </>,
    );
  }
  return <>{header}</>;
};

interface ShortestPathFromSeedSetGraphExporterPreviewRowProps {
  exporter: ShortestPathFromSeedSetGraphExporter;
  exportDatum: ShortestPathFromSeedSetExportRow;
}

const ShortestPathFromSeedSetGraphExporterPreviewRow = ({
  exporter,
  exportDatum,
}: ShortestPathFromSeedSetGraphExporterPreviewRowProps) => {
  const showAccountStatus = useFeatureFlag('dashboard:accounts-show-account-status');

  const row = [];
  const longestPath = exporter.longestPath;
  for (let hops = 0; hops <= longestPath; hops += 2) {
    const prefix = exporter.getAccountRowPartPrefix(hops);
    row.push(
      <>
        <ConstrainedTableTD>{exportDatum[`${prefix}-id`]}</ConstrainedTableTD>
        {hops > 0 && (
          <ConstrainedTableTD>{exportDatum[`${prefix}-connected-by`]}</ConstrainedTableTD>
        )}
        <ConstrainedTableTD>
          {Array.from(exportDatum[`${prefix}-name`] || []).join('; ')}
        </ConstrainedTableTD>
        <ConstrainedTableTD>{exportDatum[`${prefix}-created-at`]}</ConstrainedTableTD>
        <ConstrainedTableTD>{exportDatum[`${prefix}-reference-id`]}</ConstrainedTableTD>
        {showAccountStatus && (
          <ConstrainedTableTD>
            {exportDatum[`${prefix}-status`] && (
              <Sticker
                variant={
                  exportDatum[`${prefix}-status`] === ACTIVE_ACCOUNT_STATUS ? 'success' : 'basic'
                }
              >
                {exportDatum[`${prefix}-status`]}
              </Sticker>
            )}
          </ConstrainedTableTD>
        )}
        <ConstrainedTableTD>
          {exportDatum[`${prefix}-tags`] && <GraphTextTags tags={exportDatum[`${prefix}-tags`]} />}
        </ConstrainedTableTD>
      </>,
    );
  }

  return <NoWrapTableTR>{row}</NoWrapTableTR>;
};

interface ShortestPathFromSeedSetGraphExporterPreviewProps {
  exporter: ShortestPathFromSeedSetGraphExporter;
}

export const ShortestPathFromSeedSetGraphExporterPreview = ({
  exporter,
}: ShortestPathFromSeedSetGraphExporterPreviewProps) => {
  const hydratedExportData = exporter.result;
  return (
    <TableLayout
      dataTest='graph-explorer__export-modal__table'
      containerMaxHeight={600}
      headerTR={
        <ShortestPathFromSeedSetGraphExporterPreviewHeader longestPath={exporter.longestPath} />
      }
      isEmpty={hydratedExportData.length === 0}
      nullState={
        <TableNullState dataTestBlock='graph-explorer__export-modal__null-table'>
          <h3>No results.</h3>
        </TableNullState>
      }
    >
      {hydratedExportData.map((exportDatum, i) => (
        <ShortestPathFromSeedSetGraphExporterPreviewRow
          key={`graph_export_preview_table_row_${i}`}
          exporter={exporter}
          exportDatum={exportDatum}
        />
      ))}
    </TableLayout>
  );
};
